/* eslint-disable @typescript-eslint/no-explicit-any */
// this file is filled with a bunch of required any
// as the states can in fact be anything

import type { Reducer } from "redux";

import type { Action } from "./actionCreators";

/**
 * enumeration of Allowed Root Reducer Keys for platform that don't
 * require a partner scope
 */
const platformRootReducerKeys = ["appContext", "experiments", "uhf"];

export const isOnAllowListRootReducerKey = (key: string): boolean =>
  platformRootReducerKeys.indexOf(key) >= 0;

export type ReducerWithScope<ReducerType> = { scopes: string[]; reducer: ReducerType };

type ReducerFromScopedReducer<T> = T extends ReducerWithScope<infer R> ? R : any;
type ReturnTypeOfScopedReducer<T> = T extends ReducerWithScope<(...args: any[]) => infer R>
  ? R
  : any;
export type GetActionFromReducer<TargetReducer extends Reducer> = TargetReducer extends Reducer<
  any,
  infer Act
>
  ? Act
  : any;

export type RootReducerFromScopedReducer<
  MapType extends {
    [key: string]: ReducerWithScope<Reducer<any, any>>;
  },
> = Reducer<
  { [K in keyof MapType]: ReturnTypeOfScopedReducer<MapType[K]> },
  GetActionFromReducer<ReducerFromScopedReducer<MapType[Extract<keyof MapType, string>]>>
>;

export function withScope<ActionType extends string, Act extends Action<ActionType>, State>(
  scopes: string | string[],
  reducer: Reducer<State, Act>
): ReducerWithScope<Reducer<State, Act>> {
  return { scopes: ([] as string[]).concat(scopes), reducer };
}

function _combineReducerWithScope<
  MapType extends { [key: string]: ReducerWithScope<Reducer<any, any>> },
  State extends { [K in keyof MapType]: ReturnTypeOfScopedReducer<MapType[K]> },
>(
  scopedMap: MapType,
  additionalScopeByPartner: boolean
): ReducerWithScope<
  Reducer<
    State,
    GetActionFromReducer<ReducerFromScopedReducer<MapType[Extract<keyof State, string>]>>
  >
> {
  type ReducerKey = Extract<keyof State, string>;
  type SupportedAction = GetActionFromReducer<ReducerFromScopedReducer<MapType[ReducerKey]>>;

  const ScopeMap = new Map<string, ReducerKey[]>();
  const StateMap = new Map<ReducerKey, Reducer<State[ReducerKey], SupportedAction>>();

  for (const key in scopedMap) {
    const { scopes, reducer } = scopedMap[key];
    scopes.forEach(scope => {
      if (ScopeMap.has(scope)) {
        ScopeMap.get(scope)!.push(key);
      } else {
        ScopeMap.set(scope, [key]);
      }
    });
    StateMap.set(key, reducer);
  }

  const combined = (state: State | undefined, action: SupportedAction): State => {
    let hasStateChanged = false;

    if (state === undefined) {
      // asserting this way in order to avoid typescript worrying that State
      // could potentially be overridden as a non-object
      const initialState = {} as State;
      StateMap.forEach((reducer, key) => {
        initialState[key] = reducer(undefined, action);
      });
      return initialState;
    }

    // In either of the three scenarios we need to initialize any undefined root state.
    // createStore dispatches @@redux/INIT (handled when state is undefined)
    // store.replaceReducer dispatches @@redux/REPLACE
    // redux-devtools-extensions dispatches @@INIT when it catches and swallows @@redux/REPLACE
    if (/^(@@redux\/(INIT|REPLACE)|@@INIT)/.test(action.type)) {
      const nextState = { ...state };

      StateMap.forEach((reducer, key) => {
        const previousStateForKey = state[key];
        nextState[key] = reducer(previousStateForKey, action);
      });

      return nextState;
    }

    const { scope: actionScope, partner: actionPartner } = action;
    let nextState: State = state;

    const scopedReducerKeys = ScopeMap.get(actionScope);
    if (scopedReducerKeys) {
      scopedReducerKeys
        .filter(key =>
          additionalScopeByPartner
            ? actionPartner
              ? key === actionPartner
              : isOnAllowListRootReducerKey(key)
            : true
        )
        .forEach(key => {
          const previousStateForKey = state[key];
          const reducer = StateMap.get(key)!;
          const nextStateForKey = reducer(previousStateForKey, action);
          if (nextStateForKey !== previousStateForKey) {
            if (!hasStateChanged) {
              nextState = { ...state };
            }
            hasStateChanged = true;
          }

          nextState[key] = nextStateForKey;
        });
    }

    return nextState;
  };

  return withScope(Array.from(ScopeMap.keys()), combined);
}

export function combineReducerWithScope<
  MapType extends { [key: string]: ReducerWithScope<Reducer<any, any>> },
  State extends { [K in keyof MapType]: ReturnTypeOfScopedReducer<MapType[K]> },
>(
  scopedMap: MapType
): ReducerWithScope<
  Reducer<
    State,
    GetActionFromReducer<ReducerFromScopedReducer<MapType[Extract<keyof State, string>]>>
  >
> {
  return _combineReducerWithScope(scopedMap, false);
}

export function combineRootReducerWithScope<
  MapType extends { [key: string]: ReducerWithScope<Reducer<any, any>> },
>(scopedMap: MapType): RootReducerFromScopedReducer<MapType> {
  return _combineReducerWithScope(scopedMap, true).reducer;
}
